import React, { useState, useEffect, useRef, useContext } from "react";
import { FootPrintChartContainer } from "./FootPrintChartContainer";
import { SummaryChartContainer } from "./SummaryChartContainer";
import { TimeChartContainer } from "./TimeChartContainer";
import {
	Switch,
	Route,
	Redirect,
	NavLink,
	useLocation,
} from "react-router-dom";
import { CodesToColorMapFunc } from "../../CodesToColorMap.js";
import { HelpButton } from "../common/HelpButton";
import { HelpPopup } from "../HelpPopup";
import { Transition } from "@headlessui/react";
import {
	allowLandscape,
	isLandscape,
	resetLandscape,
	subscribeToLandscapeChange,
	toggleForceLandscape,
	unsubscribeFromLandscapeChange,
	isXamarin,
} from "../../services/mobileService";
import { FullscreenButton } from "../common/FullscreenButton";
import { TableChartContainer } from "./TableChartContainer";
import { ExtendedTableChartContainer } from "./ExtendedTableChartContainer";
import { SimpleTableChartContainer } from "./SimpleTableChartContainer";
import { isOnChartScreen } from "../../services/summaryReportsService";
import { DiseaseGroupContext } from "../../contexts/diseaseGroupContext";

export const SummaryReportCard = ({ code, def, pathRoot }) => {
	const [title, setTitle] = useState(def[0].title);
	const [subtitle, setSubtitle] = useState(def[0].subtitle);
	const [helpCode, setHelpCode] = useState(def[0].help);
	const [isShowingHelp, setIsShowingHelp] = useState(false);
	const [isShowingPage, setIsShowingPage] = useState(false);
	const [isInLandscape, setIsInlandscape] = useState(false);
	const [showFullscreenButton, setShowFullscreenButton] = useState(false);

	const isMounted = useRef(false);

	const { theme } = useContext(DiseaseGroupContext);

	const [cardTheme, setCardTheme] = useState(CodesToColorMapFunc(code, theme));

	const defaultCardColorClass = "bg-gray-400";

	const handleLandscapeChange = (value) => {
		if (isMounted.current === true) {
			setIsInlandscape(value && isOnChartScreen());
		}
	};

	const handleShowFullscreenButton = (value) => {
		if (isMounted.current === true) {
			setShowFullscreenButton(isXamarin() && value);
		}
	};

	useEffect(() => {
		isMounted.current = true;
		setIsShowingPage(true);

		const subscriptionName = "SummaryReportCard";

		subscribeToLandscapeChange(subscriptionName, handleLandscapeChange);

		isLandscape().then(handleLandscapeChange);

		return () => {
			isMounted.current = false;
			unsubscribeFromLandscapeChange(subscriptionName);
			resetLandscape();
		};
	}, []);

	const location = useLocation();

	useEffect(() => {
		if (location.pathname.includes("timechart")) {
			allowLandscape();
		} else {
			resetLandscape();
		}
	}, [location]);

	const getPath = (route) => {
		return `${pathRoot}/${route}`;
	};

	const onLinkClick = (chartDef) => {
		setTitle(chartDef.title);
		setSubtitle(chartDef.subtitle);
		setHelpCode(chartDef.help);
	};

	const getNavLinks = () => {
		const links = def.map((e, index) => {
			const activeNavStyle = (isActive) => {
				return `${
					isActive ? "text-black border-b-3" : "text-gray-400"
				} border-black sm:pb-0.5`;
			};

			return (
				<li key={`e.displayType${index}`} className={"flex-grow-0 relative"}>
					<NavLink
						to={getPath(e.code || e.type)}
						className={activeNavStyle}
						onClick={() => {
							onLinkClick(e);
						}}
						exact
					>
						{e.type}
					</NavLink>
				</li>
			);
		});

		return links;
	};

	const charts = def.map((e) => {
		let chartContainer = null;

		switch (e.displayType.toLowerCase()) {
			case "checkgrid":
				chartContainer = <SummaryChartContainer code={code} />;
				break;
			case "footprint":
				chartContainer = (
					<FootPrintChartContainer code={code} type={e.subType} />
				);
				break;
			case "timechart":
				chartContainer = (
					<TimeChartContainer
						code={code}
						selector={e.code || e.type}
						handleShowFullscreenButton={handleShowFullscreenButton}
					/>
				);
				break;
			case "table":
				if (code === "experience") {
					chartContainer = <ExtendedTableChartContainer code={code} />;
				} else {
					chartContainer = <TableChartContainer code={code} />;
				}
				break;
			case "extendedtable":
				chartContainer = <ExtendedTableChartContainer code={code} />;
				break;
			case "simpletable":
				chartContainer = <SimpleTableChartContainer code={code} />;
				break;
			default:
				chartContainer = (
					<h2 className={"text-center"}>Chart Type Not Supported!</h2>
				);
		}

		return (
			<Route exact key={e.code || e.type} path={getPath(e.code || e.type)}>
				<div
					className={`absolute inset-0 px-3 xs:px-4 ${
						e.displayType.toLowerCase().indexOf("table") >= 0
							? ""
							: "overflow-y-auto"
					} pb-4`}
				>
					{chartContainer}
				</div>
			</Route>
		);
	});

	const cardElement = (
		<div
			className={`h-full text-black max-h-panel flex-1 flex ${
				isInLandscape ? "" : "mt-3 xl:my-8"
			} flex-col`}
		>
			{/* Header area */}
			<div className={"relative text-center mb-2 px-3 xs:px-4 h-12 sm:h-14"}>
				<h2 className={"font-bold text-lg sm:text-2xl pt-1 px-9"}>{title}</h2>
				{showFullscreenButton ? (
					<div className={"absolute top-0 left-2"}>
						<FullscreenButton onClick={() => toggleForceLandscape()} />
					</div>
				) : null}
				<div className={"absolute top-0 right-4"}>
					<HelpButton
						onClick={() => setIsShowingHelp(true)}
						iconClasses={"text-3xl sm:text-4xl"}
					/>
				</div>
				<h3 className={"text-xs sm:text-sm"}>{subtitle}</h3>
			</div>

			{/* Chart area */}
			<div className={"flex-1 relative px-3 xs:px-4 mt-2"}>
				<Switch>
					{charts}
					<Redirect from={pathRoot} to={getPath(def[0].code || def[0].type)} />
				</Switch>
			</div>

			{/* Footer area */}
			{isInLandscape ? null : (
				<div className={`z-30`}>
					{def.length > 1 ? (
						<ul className="flex justify-evenly text-xs sm:text-sm font-bold border-t-2 w-full h-full pb-3 sm:pb-5 pt-2 sm:pt-4">
							{getNavLinks()}
						</ul>
					) : null}
				</div>
			)}
		</div>
	);

	return (
		<>
			<Transition
				className={"h-full max-w-panel mx-auto flex flex-col"}
				// appear={true}
				show={isShowingPage}
				enter={`transform ease-in-out duration-500`}
				enterFrom={"translate-y-full opacity-0"}
				enterTo={"translate-y-0 opacity-100"}
				leave={`transition-opacity ease-in-out duration-200`}
				leaveFrom={"opacity-100"}
				leaveTo={"opacity-0"}
			>
				{isInLandscape ? (
					<div className={"absolute inset-0 m-3"}>{cardElement}</div>
				) : (
					cardElement
				)}
			</Transition>

			<HelpPopup
				show={isShowingHelp}
				currentTheme={cardTheme}
				onClose={() => setIsShowingHelp(false)}
				helpCode={helpCode}
			/>
		</>
	);
};
